// Leben an den Scope-3-Stationen: 2–3 anklickbare Personen und ein kleines Fundstück je Station.
import * as THREE from "three"
import { COLORS } from "./palette.js"
import { at, box, cylinder, person, mat, SKIN_TONES } from "./builders.js"
import { introduce } from "./bubble.js"
import { rng } from "../game/rng.js"
import { STATION_ENTRANCES } from "./stations.js"

const HIT_MATERIAL = new THREE.MeshBasicMaterial({ visible: false })
const SCALE = 1.45

const c = (name, role, quotes, shirt = COLORS.navy600, helmet = null) => ({ name, role, quotes, shirt, helmet })

export const STATION_CREW = {
  power_plant: [
    c("Jörg", "Kraftwerksmeister", ["Grundlast ist mein zweiter Vorname.", "Wir stellen auf Wind um. Ich bleib aber."], COLORS.orange, COLORS.white),
    c("Svetlana", "Netzingenieurin", ["Netzverluste sind auch Emissionen – Kategorie 3!", "Ich hab Spannung im Blut. 380 kV."], COLORS.navy600, COLORS.lime)
  ],
  steel_mill: [
    c("Bernhard", "Hochofenmeister", ["1.500 Grad und trotzdem gute Laune.", "Wasserstoff statt Koks? Ich lern gern dazu."], COLORS.slate, COLORS.white),
    c("Dariusz", "Schmelzer", ["Bei uns ist immer Sommer.", "Schrott ist kein Müll. Schrott ist Rohstoff!"], COLORS.orange, COLORS.white),
    c("Yvonne", "Energiemanagerin", ["Wir sind der dickste Brocken in eurer Scope-3-Bilanz. Sorry!", "Grüner Stahl kostet mehr. Noch."], COLORS.green)
  ],
  chem_plant: [
    c("Dr. Konstantin", "Werkschemiker", ["Ich habe für jedes Problem eine Lösung. Eine wässrige.", "Rezyklat? Wir sind dabei."], COLORS.white),
    c("Bärbel", "Verpackungsplanerin", ["Karton statt Folie, wo es geht.", "Ihr Joghurtbecher war meine Idee."], COLORS.lime)
  ],
  component_works: [
    c("Hartmut", "Werksleiter", ["Unsere Motoren laufen in Ihren Maschinen. Seit 1978.", "EPD? Hab ich! Irgendwo."], COLORS.navy600),
    c("Ayşe", "Key-Account-Managerin", ["CO₂-Daten pro Bauteil? Ich schick Ihnen eine Excel.", "Lieferanten-Klimaziele? Wir sind dabei!"], COLORS.green)
  ],
  farm: [
    c("Heinrich", "Landwirt", ["Meine Kühe haben mehr Follower als Sie.", "Humusaufbau speichert Kohlenstoff. Wussten Sie nicht, was?"], COLORS.green, 0x8a5a36),
    c("Jule", "Agrarwissenschaftlerin", ["Methan ist hier das eigentliche Thema.", "Regenerative Landwirtschaft braucht Zeit. Und Geld."], COLORS.lime)
  ],
  quarry: [
    c("Rudi", "Sprengmeister", ["Rumms ist mein Lieblingswort.", "Klinker sparen? Ich spreng trotzdem."], COLORS.orange, COLORS.orange),
    c("Natascha", "Betontechnologin", ["Weniger Klinker, gleiche Festigkeit. Ich zeig’s Ihnen.", "Zement verursacht rund sieben bis acht Prozent der weltweiten CO₂-Emissionen."], COLORS.slate, COLORS.white)
  ],
  office_park: [
    c("Sören", "IT-Dienstleister", ["Ihre Cloud läuft bei uns. Mit Ökostrom, versprochen.", "Die Abwärme vom Rechenzentrum heizt bald das Schwimmbad."], COLORS.navy600),
    c("Henrike", "Unternehmensberaterin", ["Dienstleistungen haben pro Euro wenig CO₂. Aber Sie kaufen viele.", "Dazu hab ich ein Framework."], COLORS.red)
  ],
  logistics_hub: [
    c("Kalle", "Lkw-Fahrer", ["Ich fahr seit 30 Jahren. Bald elektrisch.", "Leerfahrten sind mein Albtraum."], COLORS.red),
    c("Mandy", "Disponentin", ["Bahn statt Lkw? Buch ich Ihnen.", "Jeder Tonnenkilometer zählt. Ich zähl mit."], COLORS.lime)
  ],
  airport: [
    c("Nadja", "Pilotin", ["Kategorie 6 an Bord, bitte anschnallen.", "Videokonferenz? Dann flieg ich halt Fracht."], COLORS.navy, COLORS.navy),
    c("Benni", "Einwinker", ["Ich winke Flugzeuge ein. Und Sie?", "Weniger Flüge, mehr Pause."], COLORS.orange)
  ],
  suburb: [
    c("Frau Schulze", "Nachbarin", ["Mein Nachbar fährt jetzt Rad. Endlich Ruhe.", "Ich pendle nicht, ich bin Rentnerin."], COLORS.red),
    c("Dominik", "Pendler", ["40 Kilometer jeden Tag. Ein Jobrad wär was.", "Deutschlandticket? Bin dabei!"], COLORS.slate),
    c("Lotta", "Schülerin", ["Fridays for Future! Ach, heute ist Mittwoch.", "Wenn ich groß bin, mach ich Klimabilanzen."], COLORS.lime)
  ],
  customers: [
    c("Dr. Brandt", "Einkaufsleiterin", ["Wir brauchen Ihre CO₂-Daten. Nach VS-Standard, bitte.", "Ohne Klimadaten keine Verlängerung. Kleiner Scherz. Oder?"], COLORS.navy600),
    c("Matteo", "Anlagenbediener", ["Ihre Maschine läuft 24/7. Der Strom auch.", "Effizienterer Antrieb? Her damit!"], COLORS.green, COLORS.white)
  ],
  recycling: [
    c("Gabi", "Wertstoffhof-Leiterin", ["Ihr Altprodukt ist mein Rohstoff.", "Trennen lohnt sich. Wirklich."], COLORS.orange),
    c("Erwin", "Sortierer", ["Ich sortiere alles. Auch meine Gedanken.", "Rücknahmesystem? Wir haben Platz!"], COLORS.green, COLORS.lime)
  ]
}

// --- Fundstücke --------------------------------------------------------------

const EGGS = {
  power_plant: {
    title: "Strom-Hamster",
    text: "Hamster Hugo liefert die Spitzenlast. Erneuerbar – aber nur bis zur Mittagspause.",
    build() {
      const g = new THREE.Group()
      const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.7, 0.07, 6, 18), mat(COLORS.steel))
      wheel.position.y = 0.8
      const hamster = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 1), mat(COLORS.orange))
      hamster.scale.set(1.3, 1, 1)
      hamster.position.set(0, 0.32, 0)
      g.add(wheel, hamster, at(box(0.1, 0.8, 0.1, COLORS.steelDark), 0, 0, -0.12))
      g.userData.animate = (time) => {
        wheel.rotation.z = -time * 5
        hamster.position.y = 0.32 + Math.abs(Math.sin(time * 14)) * 0.06
      }
      return g
    }
  },
  steel_mill: {
    title: "Abwärme-Grill",
    text: "Hier grillt die Belegschaft mit Hochofen-Abwärme. Effizienter geht’s nicht.",
    build() {
      const g = new THREE.Group()
      g.add(cylinder(0.45, 0.35, 0.7, COLORS.navy, 10))
      g.add(at(cylinder(0.5, 0.5, 0.05, COLORS.steel, 12), 0, 0.7, 0))
      for (let i = 0; i < 3; i++) {
        const sausage = new THREE.Mesh(new THREE.CapsuleGeometry(0.06, 0.4, 3, 6), mat(0xb5532e))
        sausage.rotation.z = Math.PI / 2
        sausage.position.set(0, 0.8, -0.2 + i * 0.2)
        g.add(sausage)
      }
      return g
    }
  },
  chem_plant: {
    title: "Tropfendes Rohr",
    text: "Tropft nicht, läuft nur leicht. Der Eimer wird jeden Freitag geleert.",
    build() {
      const g = new THREE.Group()
      const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 2, 8), mat(COLORS.steel))
      pipe.rotation.z = Math.PI / 2
      pipe.position.y = 1.6
      g.add(pipe, at(box(0.1, 1.6, 0.1, COLORS.steelDark), -0.9, 0, 0), at(box(0.1, 1.6, 0.1, COLORS.steelDark), 0.9, 0, 0))
      g.add(cylinder(0.25, 0.2, 0.35, COLORS.red, 10))
      const drop = new THREE.Mesh(new THREE.SphereGeometry(0.06, 5, 4), mat(COLORS.glass))
      g.add(drop)
      g.userData.animate = (time) => {
        const t = (time * 0.8) % 1
        drop.position.set(0, 1.45 - t * 1.1, 0)
      }
      return g
    }
  },
  component_works: {
    title: "Kollege Roboter",
    text: "Der neue Kollege winkt den ganzen Tag. Pausen macht er keine, Kaffee trinkt er auch nicht.",
    build() {
      const g = new THREE.Group()
      g.add(cylinder(0.35, 0.4, 0.3, COLORS.orange, 10))
      const lower = new THREE.Group()
      lower.position.y = 0.3
      lower.add(at(box(0.18, 1, 0.18, COLORS.orange), 0, 0, 0))
      const upper = new THREE.Group()
      upper.position.y = 1
      upper.add(at(box(0.14, 0.8, 0.14, COLORS.orange), 0, 0, 0))
      upper.add(at(box(0.3, 0.12, 0.2, COLORS.navy), 0, 0.8, 0))
      lower.add(upper)
      g.add(lower)
      g.userData.animate = (time) => {
        lower.rotation.z = Math.sin(time * 1.2) * 0.25
        upper.rotation.z = 0.6 + Math.sin(time * 4) * 0.5
      }
      return g
    }
  },
  farm: {
    title: "Freilaufendes Huhn",
    text: "Gack! Ich bin auch Kategorie 1 – Eier inklusive.",
    build() {
      const g = new THREE.Group()
      const chicken = new THREE.Group()
      const body = new THREE.Mesh(new THREE.IcosahedronGeometry(0.25, 1), mat(COLORS.white))
      body.scale.set(1.2, 1, 0.9)
      body.position.y = 0.35
      const head = new THREE.Mesh(new THREE.IcosahedronGeometry(0.12, 1), mat(COLORS.white))
      head.position.set(0.25, 0.6, 0)
      chicken.add(body, head, at(box(0.06, 0.1, 0.04, COLORS.red), 0.26, 0.7, 0), at(box(0.08, 0.04, 0.05, COLORS.orange), 0.37, 0.6, 0))
      chicken.add(at(box(0.04, 0.2, 0.04, COLORS.orange), 0, 0, 0.08), at(box(0.04, 0.2, 0.04, COLORS.orange), 0, 0, -0.08))
      g.add(chicken)
      g.userData.animate = (time) => {
        const a = time * 0.7
        chicken.position.set(Math.cos(a) * 1.2, Math.abs(Math.sin(time * 9)) * 0.05, Math.sin(a) * 1.2)
        chicken.rotation.y = -a - Math.PI / 2
      }
      return g
    }
  },
  quarry: {
    title: "Fossil im Kies",
    text: "Ein Fossil! Genau wie unser alter Gaskessel.",
    build() {
      const g = new THREE.Group()
      g.add(at(box(0.9, 0.35, 0.5, COLORS.white), 0.9, 0, 0))
      for (let i = 0; i < 5; i++) {
        const rib = at(box(0.08, 0.5 - Math.abs(i - 2) * 0.08, 0.6, COLORS.white), -0.6 + i * 0.25, 0, 0)
        rib.rotation.x = 0.2
        g.add(rib)
      }
      g.add(at(box(2, 0.08, 0.08, COLORS.white), 0, 0.25, 0))
      return g
    }
  },
  office_park: {
    title: "Liegestuhl",
    text: "Homeoffice auf dem Rasen. Pendelstrecke: null Kilometer.",
    build() {
      const g = new THREE.Group()
      const seat = at(box(0.6, 0.06, 1.1, COLORS.lime), 0, 0.35, 0)
      seat.rotation.x = -0.3
      g.add(seat, at(box(0.06, 0.4, 0.06, 0x8a5a36), -0.25, 0, 0.4), at(box(0.06, 0.4, 0.06, 0x8a5a36), 0.25, 0, 0.4))
      g.add(at(cylinder(0.04, 0.04, 1.8, COLORS.white, 6), 0.7, 0, -0.3))
      const umbrella = new THREE.Mesh(new THREE.ConeGeometry(0.9, 0.4, 8), mat(COLORS.red))
      umbrella.position.set(0.7, 1.9, -0.3)
      g.add(umbrella)
      return g
    }
  },
  logistics_hub: {
    title: "Wanderndes Paket",
    text: "Ich bin eine Retoure. Schon wieder. Zurück zum Absender!",
    build() {
      const g = new THREE.Group()
      const parcel = new THREE.Group()
      parcel.add(at(box(0.6, 0.45, 0.5, 0xc9a46b), 0, 0.3, 0))
      const legs = [-0.12, 0.12].map((z) => {
        const leg = at(box(0.06, 0.3, 0.06, COLORS.navy), 0, 0, z)
        parcel.add(leg)
        return leg
      })
      g.add(parcel)
      g.userData.animate = (time) => {
        parcel.position.x = Math.sin(time * 0.6) * 1.4
        parcel.rotation.y = Math.cos(time * 0.6) > 0 ? 0 : Math.PI
        legs[0].position.y = Math.max(0, Math.sin(time * 10)) * 0.08
        legs[1].position.y = Math.max(0, -Math.sin(time * 10)) * 0.08
      }
      return g
    }
  },
  airport: {
    title: "Herrenloser Koffer",
    text: "Dreht hier seit Jahren seine Runden. Reist emissionsarm – nur leider nirgendwohin.",
    build() {
      const g = new THREE.Group()
      // Kleines Gepäckband: Ring mit Mittelteil
      g.add(cylinder(1.3, 1.3, 0.35, COLORS.navy600, 18))
      g.add(at(cylinder(0.75, 0.75, 0.42, COLORS.steel, 14), 0, 0, 0))
      const suitcase = new THREE.Group()
      suitcase.add(at(box(0.55, 0.38, 0.32, COLORS.orange), 0, 0, 0))
      suitcase.add(at(box(0.2, 0.06, 0.06, COLORS.navy), 0, 0.38, 0))
      suitcase.add(at(box(0.12, 0.12, 0.33, COLORS.lime), 0.15, 0.12, 0))
      const holder = new THREE.Group()
      holder.position.y = 0.35
      suitcase.position.x = 1.02
      holder.add(suitcase)
      g.add(holder)
      g.userData.animate = (time) => {
        holder.rotation.y = time * 0.6
        suitcase.rotation.z = Math.sin(time * 5) * 0.03
      }
      return g
    }
  },
  suburb: {
    title: "Gartenzwerg",
    text: "Pendelt seit 40 Jahren null Kilometer. Vorbildlich in Kategorie 7!",
    build() {
      const g = new THREE.Group()
      g.add(cylinder(0.22, 0.28, 0.45, COLORS.navy600, 8))
      g.add(at(cylinder(0.18, 0.2, 0.15, 0xf2c9a0, 8), 0, 0.45, 0))
      g.add(at(new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.25, 6), mat(COLORS.white)), 0, 0.55, 0.12))
      const hat = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.5, 8), mat(COLORS.red))
      hat.position.y = 0.85
      g.add(hat)
      return g
    }
  },
  customers: {
    title: "Mähroboter",
    text: "Mäht seit Jahren mit Ökostrom und hat noch nie gefragt, ob er Pause machen darf.",
    build() {
      const g = new THREE.Group()
      const robot = new THREE.Group()
      robot.add(at(box(0.6, 0.18, 0.45, COLORS.green), 0, 0.05, 0), at(box(0.25, 0.06, 0.2, COLORS.navy), 0, 0.23, 0))
      g.add(robot)
      g.userData.animate = (time) => {
        const t = (((time * 0.15) % 1) + 1) % 1
        const side = Math.floor(t * 4)
        const f = (t * 4) % 1
        const corners = [[-1.2, -0.8], [1.2, -0.8], [1.2, 0.8], [-1.2, 0.8]]
        const [x0, z0] = corners[side]
        const [x1, z1] = corners[(side + 1) % 4]
        robot.position.set(x0 + (x1 - x0) * f, 0, z0 + (z1 - z0) * f)
        robot.rotation.y = -Math.atan2(z1 - z0, x1 - x0)
      }
      return g
    }
  },
  recycling: {
    title: "Waschbär in der Tonne",
    text: "Kreislaufwirtschaft? Ich nenn das Abendessen.",
    build() {
      const g = new THREE.Group()
      g.add(cylinder(0.4, 0.35, 0.9, COLORS.navy600, 10))
      const raccoon = new THREE.Group()
      raccoon.add(new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 1), mat(COLORS.slate)))
      raccoon.add(at(box(0.36, 0.08, 0.05, COLORS.navy), 0, -0.02, 0.19))
      for (const x of [-0.12, 0.12]) raccoon.add(at(box(0.08, 0.1, 0.06, COLORS.slate), x, 0.15, 0))
      g.add(raccoon)
      g.userData.animate = (time) => {
        raccoon.position.y = 0.85 + Math.max(0, Math.sin(time * 0.9)) * 0.25
        raccoon.rotation.y = Math.sin(time * 1.3) * 0.5
      }
      return g
    }
  }
}

// --- Platzierung ---------------------------------------------------------------

function freeSpots(group, roadDirection, random, entrance = [0, 0], blockedAt = () => false) {
  group.updateMatrixWorld(true)
  const inverse = new THREE.Matrix4().copy(group.matrixWorld).invert()
  const obstacles = group.children
    .map((child) => ({ child, box: new THREE.Box3().setFromObject(child).applyMatrix4(inverse) }))
    .filter(({ child, box }) => !box.isEmpty() && box.min.y < 0.5 && (box.max.y > 0.2 || child.userData.keepFree))
    .map(({ box }) => {
      box.max.y = Math.max(box.max.y, 1)
      return box.expandByScalar(0.6)
    })
  const spots = []
  for (let i = 0; i < 400 && spots.length < 8; i++) {
    const p = new THREE.Vector3((random() * 2 - 1) * 8, 0, 1 + random() * 7)
    const probe = new THREE.Vector3(p.x, 0.5, p.z)
    if (obstacles.some((box) => box.containsPoint(probe))) continue
    // z. B. die Bahnstrecke am Logistikzentrum (Weltkoordinaten)
    if (blockedAt(group.position.x + p.x, group.position.z + p.z)) continue
    // nicht auf der Zufahrt stehen
    const dx = p.x - entrance[0]
    const dz = p.z - entrance[1]
    const along = dx * roadDirection.x + dz * roadDirection.z
    const across = Math.abs(dx * roadDirection.z - dz * roadDirection.x)
    if (along > -2 && across < 1.8) continue
    if (spots.some((s) => s.distanceTo(p) < 1.8)) continue
    spots.push(p)
  }
  // nach vorne (zur Kamera) sortieren
  return spots.sort((a, b) => b.z - a.z)
}

export function populateStation(id, group, blockedAt) {
  const crew = STATION_CREW[id] || []
  const eggConfig = EGGS[id]
  const random = rng(id.length * 131 + id.charCodeAt(0))
  const entrance = STATION_ENTRANCES[id] || [0, 0]
  const toCenter = new THREE.Vector3(-group.position.x - entrance[0], 0, -group.position.z - entrance[1]).normalize()
  const spots = freeSpots(group, toCenter, random, entrance, blockedAt)

  const figures = crew.slice(0, Math.max(0, spots.length - 1)).map((character, i) => {
    const figure = person({ shirt: character.shirt, helmet: character.helmet, skin: SKIN_TONES[(i * 2 + id.length) % SKIN_TONES.length] })
    figure.scale.setScalar(SCALE)
    figure.position.copy(spots[i + 1])
    figure.rotation.y = (random() - 0.5) * 1.2
    const zone = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 1.3, 8), HIT_MATERIAL)
    zone.position.y = 0.65
    figure.add(zone)
    figure.userData.stationPerson = true
    figure.userData.character = character
    figure.userData.phase = random() * Math.PI * 2
    group.add(figure)
    return figure
  })

  let egg = null
  if (eggConfig && spots[0]) {
    egg = eggConfig.build()
    egg.scale.setScalar(SCALE)
    egg.position.copy(spots[0])
    egg.userData.egg = { id: `station_${id}`, title: eggConfig.title, text: eggConfig.text }
    const zone = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 2.5, 10), HIT_MATERIAL)
    zone.position.y = 1.25
    egg.add(zone)
    group.add(egg)
  }

  return {
    figures,
    egg,
    update(dt, time) {
      for (const figure of figures) figure.position.y = Math.max(0, Math.sin(time * 2 + figure.userData.phase)) * 0.04
      egg?.userData.animate?.(time)
    },
    poke(figure) {
      const quotes = figure.userData.character.quotes
      let quote = quotes[Math.floor(Math.random() * quotes.length)]
      if (quote === figure.userData.lastQuote && quotes.length > 1) quote = quotes.find((q) => q !== quote)
      figure.userData.lastQuote = quote
      introduce(figure, figure.userData.character, quote, { y: 1.6 })
    }
  }
}
